import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send } from 'lucide-react';
import { businessInfo } from '../mockData';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    device: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Mock form submission
    console.log('Form submitted:', formData);
    
    toast({
      title: "Melding sendt!",
      description: "Vi kommer tilbake til deg så snart som mulig.",
    });

    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      device: '',
      message: ''
    });
  };

  return (
    <div className="min-h-screen pt-32 pb-16">
      {/* Hero */}
      <section className="bg-gradient-to-br from-sky-50 to-cyan-50 py-16 mb-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Kontakt <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-600 to-cyan-600">Oss</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Vi er her for å hjelpe deg. Ta kontakt i dag!
          </p>
        </div>
      </section>

      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="border-2">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Send oss en melding
                </h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name">Navn *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Ditt navn"
                        required
                        className="border-2 focus:border-sky-600"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">E-post *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="din@epost.no"
                        required
                        className="border-2 focus:border-sky-600"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Telefon</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="Ditt telefonnummer"
                        className="border-2 focus:border-sky-600"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="device">Enhet</Label>
                      <Input
                        id="device"
                        name="device"
                        value={formData.device}
                        onChange={handleChange}
                        placeholder="F.eks. iPhone 14, Samsung S23"
                        className="border-2 focus:border-sky-600"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Melding *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Beskriv problemet ditt eller hva du trenger hjelp med..."
                      required
                      rows={6}
                      className="border-2 focus:border-sky-600 resize-none"
                    />
                  </div>

                  <Button 
                    type="submit"
                    className="w-full bg-gradient-to-r from-sky-600 to-cyan-600 hover:from-sky-700 hover:to-cyan-700 text-white py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all"
                  >
                    <Send size={20} className="mr-2" />
                    Send melding
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Contact Info Sidebar */}
          <div className="space-y-6">
            {/* Phone */}
            <Card className="border-2 hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="bg-gradient-to-br from-sky-100 to-cyan-100 p-3 rounded-xl">
                    <Phone size={24} className="text-sky-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Telefon</h3>
                    <a 
                      href={`tel:${businessInfo.phone}`}
                      className="text-sky-600 hover:text-sky-700 font-medium"
                    >
                      {businessInfo.phone}
                    </a>
                    <p className="text-sm text-gray-500 mt-1">Ring oss i dag!</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Email */}
            <Card className="border-2 hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="bg-gradient-to-br from-sky-100 to-cyan-100 p-3 rounded-xl">
                    <Mail size={24} className="text-sky-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">E-post</h3>
                    <a 
                      href={`mailto:${businessInfo.email}`}
                      className="text-sky-600 hover:text-sky-700 font-medium break-all"
                    >
                      {businessInfo.email}
                    </a>
                    <p className="text-sm text-gray-500 mt-1">Send oss en epost</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Address */}
            <Card className="border-2 hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="bg-gradient-to-br from-sky-100 to-cyan-100 p-3 rounded-xl">
                    <MapPin size={24} className="text-sky-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Adresse</h3>
                    <p className="text-gray-600">{businessInfo.address}</p>
                    <p className="text-sm text-gray-500 mt-1">{businessInfo.location}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Hours */}
            <Card className="border-2 hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="bg-gradient-to-br from-sky-100 to-cyan-100 p-3 rounded-xl">
                    <Clock size={24} className="text-sky-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Åpningstider</h3>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between gap-4">
                        <span className="text-gray-600">Man-Fre:</span>
                        <span className="font-medium text-gray-900">{businessInfo.openingHours.weekdays}</span>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span className="text-gray-600">Lør:</span>
                        <span className="font-medium text-gray-900">{businessInfo.openingHours.saturday}</span>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span className="text-gray-600">Søn:</span>
                        <span className="font-medium text-gray-900">{businessInfo.openingHours.sunday}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Map */}
        <Card className="mt-8 border-2">
          <CardContent className="p-0">
            <div className="aspect-[21/9] bg-gray-100 rounded-lg overflow-hidden">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2000.3!2d10.953!3d59.919!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sStoperiveien%205%2C%202010%20Strommen%2C%20Norge!5e0!3m2!1sen!2sno!4v1"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                title="DrMobil Strommen Location Map"
              ></iframe>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Contact;
